import { useCallback, useState } from "react";
import { Box, Text, Image } from "@mantine/core";
import {
  formatLabCalendarDate,
  formatLabClockTime24,
} from "../../utils/labTime";

interface SignatureBlockSectionProps {
  isSigned: boolean;
  signedBy?: string;
  signedAt?: string;
  credentials?: string;
  signatureImageUrl?: string;
  reviewSimulation?: boolean;
}

const sigFontFamily = "'Times New Roman', Times, serif";

const sigLabelStyle: React.CSSProperties = {
  fontFamily: sigFontFamily,
  fontSize: "9pt",
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: 0,
  color: "var(--mantine-color-black)",
};

const sigNameStyle: React.CSSProperties = {
  fontFamily: sigFontFamily,
  fontSize: "10pt",
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: 0,
  color: "var(--mantine-color-black)",
};

const sigDateStyle: React.CSSProperties = {
  fontFamily: sigFontFamily,
  fontSize: "10pt",
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: 0,
  color: "var(--mantine-color-black)",
};

function formatSignatureDate(dateStr: string): string {
  return `${formatLabCalendarDate(dateStr)} ${formatLabClockTime24(dateStr)}`;
}

export function SignatureBlockSection({
  isSigned,
  signedBy,
  signedAt,
  credentials,
  signatureImageUrl,
  reviewSimulation = false,
}: SignatureBlockSectionProps) {
  const [imageError, setImageError] = useState(false);
  const handleImageError = useCallback(() => setImageError(true), []);

  if ((!isSigned && !reviewSimulation) || !signedBy) {
    return (
      <Box
        mt={12}
        style={{
          borderTop: "1px solid var(--mantine-color-gray-5)",
          paddingTop: "5pt",
        }}
      >
        <Text size="sm" c="dimmed" fs="italic" ta="center">
          Signature will appear upon sign-out
        </Text>
      </Box>
    );
  }

  return (
    <Box
      mt={12}
      data-testid="signature-footprint"
      style={{
        borderTop: "1px solid var(--mantine-color-gray-5)",
        minHeight: "54pt",
        paddingTop: "5pt",
      }}
    >
      <Box
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-start",
          gap: "6pt",
        }}
      >
        <span style={sigLabelStyle}>ELECTRONICALLY SIGNED BY:</span>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {isSigned && signatureImageUrl && !imageError && (
            <Image
              src={signatureImageUrl}
              alt="Pathologist signature"
              fit="contain"
              style={{
                height: "33.4pt",
                width: "auto",
                maxWidth: "185.5pt",
                display: "block",
              }}
              onError={handleImageError}
            />
          )}
          <span style={sigNameStyle}>
            {signedBy}
            {credentials ? `, ${credentials}` : ""}
          </span>
          {reviewSimulation && !isSigned && (
            <span style={sigDateStyle}>Signature applied at sign-out</span>
          )}
          {signedAt && (
            <span style={sigDateStyle}>{formatSignatureDate(signedAt)}</span>
          )}
        </Box>
      </Box>
    </Box>
  );
}
